import React, { useState } from "react";
import { Modal, Button } from "antd";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

const cleanAndFormatHeaders = (rows) => {
  if (!rows || rows.length === 0) return [];

  // Define keys to exclude (case-insensitive checks, space/underscore ignored)
  const excludeKeys = new Set([
    "key",
    "originalstatus",
    "originalrisk",
    "statuscolor",
    "riskcolor",
    "risktextcolor",
    "textcolor"
  ]);

  const firstRowKeys = Object.keys(rows[0]);
  const isAppointments = firstRowKeys.includes("timeSlot") || firstRowKeys.includes("practitioner");

  const HEADER_MAP = {
    pcUserId: "USER ID",
    apptId: "APPT ID",
    id: "APPT ID",
    timeSlot: "TIME SLOT",
    memberName: "MEMBER NAME",
    joinedSince: "JOINED SINCE",
    riskStatus: "RISK STATUS",
    risk: "RISK STATUS",
    availableDays: "AVAILABILITY DAYS",
    numberOfConsultations: "CONSULTATIONS",
    mobile: "CONTACT",
  };

  // Map camelCase or custom keys to capital headers
  const getHeaderName = (key) => {
    if (key === "status" && isAppointments) {
      return "APPOINTMENT STATUS";
    }

    return (
      HEADER_MAP[key] ||
      key
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .trim()
        .toUpperCase()
    );
  };

  return rows.map((row) => {
    const newRow = {};
    Object.keys(row).forEach((key) => {
      const normalizedKey = key.toLowerCase().replace(/[\s_]+/g, "");
      // Exclude matches
      if (excludeKeys.has(normalizedKey)) {
        return;
      }
      
      const newKey = getHeaderName(key);
      newRow[newKey] = row[key];
    });
    return newRow;
  });
};

export default function ExportModal({
  visible,
  onClose,
  data = [],
  allData = [],
  onFetchAllData,
  selectedRows = []
}) {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [exportRange, setExportRange] = useState("visible");
  const [loading, setLoading] = useState(false);

  console.log(selectedRows);

  const handleExport = async () => {
    if (!selectedFormat) return;

    let exportDataset = data;

    if (exportRange === "all") {
      if (allData && allData.length > 0) {
        exportDataset = allData;
      } else if (onFetchAllData) {
        setLoading(true);
        try {
          exportDataset = await onFetchAllData();
        } catch (error) {
          console.error("Failed to fetch all data:", error);
          setLoading(false);
          return;
        }
        setLoading(false);
      }
    }

    // If no rows are selected, export all data in the selected range
    const filteredData =
      selectedRows.length > 0
        ? exportDataset.filter((row) => selectedRows.includes(row.key))
        : exportDataset;

    if (filteredData.length === 0) {
      console.warn("No data found for export");
      return;
    }

    // Clean columns and format headers to bold & capital
    const formattedDataForExport = cleanAndFormatHeaders(filteredData);

    if (selectedFormat === "excel") exportExcel(formattedDataForExport);
    if (selectedFormat === "csv") exportCSV(formattedDataForExport);
    if (selectedFormat === "pdf") exportPDF(formattedDataForExport);
  };

  const exportExcel = (rows) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    
    // Apply styling to headers (row 0)
    if (rows && rows.length > 0) {
      const headers = Object.keys(rows[0]);
      headers.forEach((_, c) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = {
            font: {
              bold: true,
              name: "Calibri",
              sz: 11
            },
            fill: {
              fgColor: { rgb: "F2F2F2" }
            },
            alignment: {
              horizontal: "left",
              vertical: "center"
            }
          };
        }
      });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "export.xlsx");
  };

  const exportCSV = (rows) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "export.csv");
  };

  const exportPDF = (rows) => {
    const htmlContent = `
      <html>
        <head>
          <title>Export PDF</title>
          <style>
            table {
              border-collapse: collapse;
              width: 100%;
              font-family: Arial, sans-serif;
            }
            th {
              font-weight: bold;
              text-transform: uppercase;
              background-color: #f2f2f2;
              padding: 8px;
              text-align: left;
              border: 1px solid #dddddd;
            }
            td {
              padding: 8px;
              text-align: left;
              border: 1px solid #dddddd;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                ${Object.keys(rows[0] || {})
                  .map((key) => `<th>${key}</th>`)
                  .join("")}
              </tr>
            </thead>
            <tbody>
              ${rows
                .map(
                  (row) =>
                    `<tr>${Object.values(row)
                      .map((val) => `<td>${val === null || val === undefined ? "" : val}</td>`)
                      .join("")}</tr>`
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open("", "", "height=600,width=800");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      closeIcon={<span className="text-xl text-gray-400 font-poppins hover:text-gray-600">×</span>}
    >
      <div className="p-2 font-poppins">
        {/* Header */}
        <h2 className="text-2xl font-bold text-[#171717] font-poppins">Export Records</h2>
        <p className="text-[#64748B] text-sm mt-1 mb-4 font-poppins">
          Easily download selected members in your preferred format.
        </p>

        <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#E2E8F0] my-4" />

        {/* Section 1: Select Data Range */}
        <h3 className="text-base font-semibold text-[#171717] font-poppins mb-3">Select Data Range</h3>
        <div className="flex gap-6 mb-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer font-poppins text-sm text-[#334155]">
            <input
              type="radio"
              name="exportRange"
              value="visible"
              checked={exportRange === "visible"}
              onChange={(e) => setExportRange(e.target.value)}
              className="w-4 h-4 accent-[#4B61FF]"
            />
            Current page
          </label>
          <label className="flex items-center gap-2 cursor-pointer font-poppins text-sm text-[#334155]">
            <input
              type="radio"
              name="exportRange"
              value="all"
              checked={exportRange === "all"}
              onChange={(e) => setExportRange(e.target.value)}
              className="w-4 h-4 accent-[#4B61FF]"
            />
            All data
          </label>
        </div>

        <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#E2E8F0] my-4" />

        {/* Section 2: Choose Format */}
        <h3 className="text-base font-semibold text-[#171717] font-poppins mb-1">Choose Format</h3>
        <p className="text-[#64748B] text-sm mb-4 font-poppins">
          Choose the format that suits your reporting or analysis needs.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {["excel", "csv", "pdf"].map((format) => (
            <button
              key={format}
              type="button"
              className={`py-3 px-4 rounded-xl border-[1px] border-solid text-sm font-semibold transition font-poppins text-center cursor-pointer ${selectedFormat === format
                ? "bg-[#E8F2FF] border-[#B3D4FF] text-[#4B61FF]"
                : "bg-[#F8FAFC] border-[#E2E8F0] text-[#334155] hover:bg-gray-100"
                }`}
              onClick={() => setSelectedFormat(format)}
            >
              {format === "excel"
                ? "Excel (.xlsx)"
                : format === "csv"
                  ? "CSV (.csv)"
                  : "PDF (.pdf)"}
            </button>
          ))}
        </div>

        <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#E2E8F0] my-4" />

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <Button
            onClick={onClose}
            className="flex-1 h-auto py-3 border-[1px] border-solid border-[#E2E8F0] rounded-xl text-base font-semibold text-[#334155] bg-white hover:bg-gray-50 font-poppins"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="flex-1 h-auto py-3 btn-gradient rounded-xl text-base font-semibold border-0 font-poppins"
            disabled={!selectedFormat || loading}
            loading={loading}
            onClick={handleExport}
          >
            Export Data
          </Button>
        </div>
      </div>
    </Modal>
  );
}
